import { PosApiError } from "@/lib/api/client";
import { resetPosContext } from "@/lib/hooks/usePosSession";
import { LIVE } from "@/lib/data/source";
import type { Problem } from "@/lib/api/types";

/** A counter the signed-in member of staff may open. */
export interface PosCounter {
  id: string;
  name: string;
}

/**
 * Either the session was opened, or a counter has to be chosen first.
 *
 * `counters` is only ever populated when the credentials were ACCEPTED — being asked which counter
 * you are working is not a failed sign-in.
 */
/**
 * How a counter sign-in can end.
 *
 * Three shapes, not two: a session; the counters this person may open; or a second factor still
 * owed. All three arrive as 200, because two of them are questions rather than rejections.
 */
export type PosSignInResult =
  | { opened: true; mfaOffer?: { daysLeft: number } }
  | { opened: false; counters: PosCounter[] }
  | { opened: false; challengeToken: string; enrolmentRequired: boolean };

/**
 * Establish a live POS counter session scoped to a counter.
 *
 * A POS counter session must carry a `locationId` claim (ADR-0015 D8) — the edge BFF rejects
 * requests without it (422 INVALID_POS_CONTEXT).
 *
 * Sign-in is TWO steps, and the second is usually skipped. Credentials go up without a counter;
 * the server answers with a session when this person works exactly one counter, or with the list to
 * choose from when they work several. The counter list used to be a hardcoded fixture shown before
 * anyone had signed in, which offered counters the tenant does not have — picking one failed with
 * "Staff is not assigned to this POS location", which reads as a permissions problem rather than a
 * counter that was never real.
 *
 * Posts to /pos-auth/login, which next.config proxies to pos-orchestrator's own auth edge
 * (POST /pos/auth/login), relayed unchanged to user-management, which validates the
 * staff/counter pair and sets the PERKSMITH_AUTH cookie (ADR-0015 D1).
 *
 * In mock mode this is a no-op so the design build still flows locally.
 */
/**
 * Forget which vertical the last operator was working in.
 *
 * A till is shared. `pos.activePack` is remembered in localStorage so the shared back-office
 * screens can keep an operator inside their own business as they walk around, and localStorage
 * outlives the session that wrote it — so the gym manager's rail was still on screen after a
 * retail cashier signed in on the same terminal, offering Door check-in, Classes and Plans to a
 * counter whose own context says `packs: ["retail"]`. That is the failure ADR-0036 D1 exists to
 * prevent, arriving through the browser rather than through the resolver.
 *
 * Cleared at both ends of a session for the same reason the refresh token is revoked at sign-out
 * rather than merely dropped: what the last person was doing is not the next person's business.
 */
function forgetActivePack(): void {
  if (typeof window !== "undefined") window.localStorage.removeItem("pos.activePack");
  // The counter's context is cached for the page load (see `posContext`). Sign-in is a client
  // navigation, so without this the next operator would render against the last one's counter —
  // their location, their role and their words.
  resetPosContext();
}

export async function establishPosSession(
  identifier: string,
  password: string,
  locationId?: string,
): Promise<PosSignInResult> {
  if (!LIVE) return { opened: true };
  const res = await fetch(`/pos-auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier, password, locationId: locationId ?? null }),
  });
  if (!res.ok) {
    let problem: Problem;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    throw new PosApiError(res.status, problem);
  }

  // The server answers a sign-in in one of THREE ways, all 200, because two of them are questions
  // rather than rejections: a session, the counters this person may open, or a demand for a second
  // factor. Reading only the first two is how a challenge would look like a successful sign-in.
  const body = (await res.json().catch(() => null)) as {
    data?: {
      counters?: PosCounter[];
      mfaRequired?: boolean;
      enrolmentRequired?: boolean;
      challengeToken?: string;
      // Present only while a second factor is still optional for this account. The sign-in has
      // SUCCEEDED -- this rides along on a real session, so dropping it costs nothing but the offer.
      mfaOffer?: { daysLeft: number };
    };
  } | null;

  if (body?.data?.mfaRequired && body.data.challengeToken) {
    forgetActivePack();
    return {
      opened: false,
      challengeToken: body.data.challengeToken,
      enrolmentRequired: Boolean(body.data.enrolmentRequired),
    };
  }

  const counters = body?.data?.counters;
  if (!counters || counters.length === 0) forgetActivePack();
  if (counters && counters.length > 0) return { opened: false, counters };
  return { opened: true, mfaOffer: body?.data?.mfaOffer };
}

/**
 * Step two of a counter sign-in: the six digits, or a recovery code.
 *
 * <p>Its own owner endpoint rather than the console's. A till's session must carry the
 * {@code locationId} claim, and the console verify issues one without it — which authenticates
 * fine and then answers 422 to every call the counter makes.
 *
 * <p>Returns the same shapes the first step does, so a counter that still has to be chosen is
 * chosen after the code exactly as it would have been before it.
 */
export async function completePosSecondFactor(
  challengeToken: string,
  code: string,
  locationId?: string,
): Promise<PosSignInResult> {
  const res = await fetch(`/pos-auth/mfa/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challengeToken, code, locationId: locationId ?? null }),
  });
  if (!res.ok) {
    let problem: Problem;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    throw new PosApiError(res.status, problem);
  }
  const body = (await res.json().catch(() => null)) as { data?: { counters?: PosCounter[] } } | null;
  const counters = body?.data?.counters;
  return counters && counters.length > 0 ? { opened: false, counters } : { opened: true };
}

/** What an authenticator app needs: the secret to type, and the otpauth URI to render as a QR. */
export interface PosForcedEnrolment {
  secret: string;
  provisioningUri: string;
}

/**
 * Starts a forced enrolment for a till that answered `enrolmentRequired`: past the deadline,
 * un-enrolled, and the credentials were right, but there is no session and so no back office this
 * cashier can reach to set two-factor up. The same public `/mfa/enroll-challenge` endpoint the
 * consoles use, reached through this app's rewrite prefix -- the challenge token stands in for the
 * session it does not have.
 *
 * Unlike {@link completePosSecondFactor}, this is NOT the POS-specific endpoint: it carries no
 * `locationId`, because enrolling has nothing to do with which counter is chosen.
 */
export async function beginPosForcedEnrolment(challengeToken: string): Promise<PosForcedEnrolment> {
  const res = await fetch(`/pos-auth/mfa/enroll-challenge`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challengeToken }),
  });
  if (!res.ok) {
    let problem: Problem;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    throw new PosApiError(res.status, problem);
  }
  const body = (await res.json().catch(() => null)) as { data?: PosForcedEnrolment } | null;
  if (!body?.data?.secret || !body.data.provisioningUri) {
    throw new PosApiError(res.status, { code: "MFA_ENROLL_FAILED", message: "Setup could not be started." });
  }
  return body.data;
}

/**
 * Confirms that forced enrolment: turns two-factor on, hands back the recovery codes once, and
 * signs the till in -- same challenge token, no second sign-in.
 *
 * The session this sets is the general one `/mfa/confirm-challenge` always issues, not the
 * locationId-bearing one `completePosSecondFactor` gets from the POS-specific verify endpoint. A
 * cashier assigned to more than one counter may still need to be asked which, same as any other
 * counter sign-in that reaches `goToDestination` without one.
 */
export async function confirmPosForcedEnrolment(challengeToken: string, code: string): Promise<string[]> {
  const res = await fetch(`/pos-auth/mfa/confirm-challenge`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challengeToken, code }),
  });
  if (!res.ok) {
    let problem: Problem;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = { code: `HTTP_${res.status}`, message: res.statusText };
    }
    throw new PosApiError(res.status, problem);
  }
  forgetActivePack();
  const body = (await res.json().catch(() => null)) as { data?: { recoveryCodes?: string[] } } | null;
  return body?.data?.recoveryCodes ?? [];
}

/**
 * End the counter session.
 *
 * Posts to the service that issued it so the refresh token is REVOKED, not just forgotten by this
 * browser — a till is shared, and a session that survives sign-out on the server is a session
 * somebody else can still spend against. The reload afterwards lets the route gate do its job
 * instead of leaving a signed-out operator looking at a screen full of live data.
 */
export async function signOut(): Promise<void> {
  if (LIVE) {
    try {
      await fetch(`/pos-auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      // Ignored on purpose: the operator asked to leave, and refusing to sign somebody out because
      // the server was unreachable is the wrong side to fail on.
      //
      // Nothing below clears a cookie, and nothing here can: PERKSMITH_AUTH is HttpOnly, so only the
      // server that set it can end it -- which it does, answering the logout with
      // `Set-Cookie: PERKSMITH_AUTH=; Max-Age=0`. This comment used to claim the clear happened below,
      // which sent a reader looking for a line that does not exist and made a working sign-out
      // look like the cause of a loop it had nothing to do with.
    }
  }
  forgetActivePack();
  window.location.assign("/counter");
}
